import {
  Box,
  Stack,
  type StackProps,
  styled,
  Typography,
  type TypographyProps,
} from "@mui/material";

export interface IndicatorProps extends Omit<StackProps, "color"> {
  color?: TypographyProps["color"];
}

const Ellipse = styled(Box)<Pick<IndicatorProps, "color">>(
  ({ color, theme }) => {
    let backgroundColor = theme.palette.primary.main;

    if (color) {
      if (typeof color === "string") {
        // Handle nested palette colors like "secondary.light", "text.primary"
        if (color.includes(".")) {
          const [paletteKey, shade] = color.split(".");
          if (paletteKey in theme.palette) {
            const paletteColor =
              theme.palette[paletteKey as keyof typeof theme.palette];
            if (
              paletteColor &&
              typeof paletteColor === "object" &&
              shade in paletteColor
            ) {
              backgroundColor = paletteColor[
                shade as keyof typeof paletteColor
              ] as string;
            }
          }
        } else if (color in theme.palette) {
          // Handle simple palette color keys like "primary", "secondary"
          const paletteColor =
            theme.palette[color as keyof typeof theme.palette];
          if (
            paletteColor &&
            typeof paletteColor === "object" &&
            "main" in paletteColor
          ) {
            backgroundColor = paletteColor.main as string;
          }
        } else {
          // Direct color value (hex, rgb, etc.)
          backgroundColor = color;
        }
      } else {
        backgroundColor = color as string;
      }
    }

    return {
      width: "16px",
      height: "16px",
      backgroundColor,
      borderRadius: "50%",
    };
  },
);

export function ChartIndicator({
  color = "primary",
  children,
  ...props
}: IndicatorProps) {
  return (
    <Stack direction="row" gap={0.5} {...props}>
      <Ellipse color={color} />
      <Typography variant="caption" height={17}>
        {children}
      </Typography>
    </Stack>
  );
}
